import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { 
  CreditCard, RefreshCw, Search, Calendar, Wallet, Plus, 
  CheckCircle2, Clock, AlertTriangle, User, Building2
} from 'lucide-react';

const CreditosPage = () => {
  const { empresaActual } = useApp();
  const [creditos, setCreditos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cuentas, setCuentas] = useState([]);
  
  // Filtros
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('pendiente');
  
  // Modal pago
  const [pagoModalOpen, setPagoModalOpen] = useState(false);
  const [creditoSeleccionado, setCreditoSeleccionado] = useState(null);
  const [pagosCredito, setPagosCredito] = useState([]);
  const [nuevoPago, setNuevoPago] = useState({ cuenta_id: '', monto: '', medio_pago: 'TRANSFERENCIA', nota: '' });
  const [loadingPago, setLoadingPago] = useState(false);

  // Medios de pago disponibles
  const mediosPago = [
    { value: 'EFECTIVO', label: 'Efectivo' },
    { value: 'TRANSFERENCIA', label: 'Transferencia' },
    { value: 'YAPE', label: 'Yape' },
    { value: 'PLIN', label: 'Plin' },
    { value: 'DEPOSITO', label: 'Depósito' },
    { value: 'TARJETA', label: 'Tarjeta' },
    { value: 'OTRO', label: 'Otro' }
  ];

  // Formatear monto
  const formatMonto = (monto) => {
    return parseFloat(monto || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Cargar cuentas
  useEffect(() => {
    const cargarCuentas = async () => {
      if (!empresaActual?.id) return;
      try {
        const res = await api.get('/cuentas', { params: { empresa_id: empresaActual.id } });
        setCuentas(res.data);
      } catch (error) {
        console.error('Error cargando cuentas:', error);
      }
    };
    cargarCuentas();
  }, [empresaActual?.id]);

  // Cargar créditos
  const cargarCreditos = useCallback(async () => {
    if (!empresaActual?.id) return;
    setLoading(true);
    try {
      const res = await api.get('/creditos', { params: { empresa_id: empresaActual.id } });
      setCreditos(res.data);
    } catch (error) {
      toast.error('Error al cargar créditos');
    } finally {
      setLoading(false);
    }
  }, [empresaActual?.id]);

  useEffect(() => {
    cargarCreditos();
  }, [cargarCreditos]);

  // Abrir modal de pagos
  const abrirModalPago = async (credito) => {
    setCreditoSeleccionado(credito);
    setPagoModalOpen(true);
    setNuevoPago({ cuenta_id: '', monto: '', medio_pago: 'TRANSFERENCIA', nota: '' });
    
    try {
      const res = await api.get(`/creditos/${credito.id}/pagos`);
      setPagosCredito(res.data.pagos);
    } catch (error) {
      setPagosCredito([]);
    }
  };

  // Registrar pago
  const registrarPago = async () => {
    if (!nuevoPago.cuenta_id) {
      toast.error('Selecciona una cuenta');
      return;
    }
    
    const monto = parseFloat(nuevoPago.monto);
    if (!monto || monto <= 0) {
      toast.error('Ingresa un monto válido');
      return;
    }
    
    // Validar que no exceda el saldo pendiente
    if (monto > creditoSeleccionado.saldo_pendiente + 0.01) {
      toast.error(`El monto no puede exceder el saldo pendiente (S/. ${formatMonto(creditoSeleccionado.saldo_pendiente)})`);
      return;
    }
    
    setLoadingPago(true);
    try {
      await api.post(`/creditos/${creditoSeleccionado.id}/pagos`, {
        cuenta_id: parseInt(nuevoPago.cuenta_id),
        monto: monto,
        medio_pago: nuevoPago.medio_pago,
        nota: nuevoPago.nota
      });
      toast.success('Pago registrado correctamente');
      setNuevoPago({ cuenta_id: '', monto: '', medio_pago: 'TRANSFERENCIA', nota: '' });
      
      // Recargar pagos y créditos
      const res = await api.get(`/creditos/${creditoSeleccionado.id}/pagos`);
      setPagosCredito(res.data.pagos);
      
      // Actualizar saldo en el crédito seleccionado
      const newSaldo = creditoSeleccionado.saldo_pendiente - monto;
      setCreditoSeleccionado({
        ...creditoSeleccionado,
        saldo_pendiente: newSaldo,
        total_pagado: creditoSeleccionado.total_pagado + monto
      });
      
      cargarCreditos();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al registrar pago';
      toast.error(errorMsg);
    } finally {
      setLoadingPago(false);
    }
  };

  // Filtrar créditos
  const creditosFiltrados = creditos.filter(c => {
    const matchBusqueda = !busqueda || 
      c.name?.toLowerCase().includes(busqueda.toLowerCase()) ||
      c.partner_name?.toLowerCase().includes(busqueda.toLowerCase()) ||
      c.num_comp?.toLowerCase().includes(busqueda.toLowerCase());
    
    const matchEstado = filtroEstado === 'all' || 
      (filtroEstado === 'pendiente' && c.saldo_pendiente > 0) ||
      (filtroEstado === 'pagado' && c.saldo_pendiente <= 0);
    
    return matchBusqueda && matchEstado;
  });

  // Resumen
  const totalCreditos = creditos.reduce((sum, c) => sum + (c.amount_total || 0), 0);
  const totalPagado = creditos.reduce((sum, c) => sum + (c.total_pagado || 0), 0);
  const totalPendiente = creditos.reduce((sum, c) => sum + (c.saldo_pendiente || 0), 0);

  if (!empresaActual) {
    return (
      <div className="p-8 text-center text-gray-500">
        Selecciona una empresa para ver los créditos
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <CreditCard className="w-7 h-7 text-blue-600" />
            Créditos por Cobrar
          </h1>
          <p className="text-gray-500 text-sm">Ventas a crédito pendientes de pago</p>
        </div>
        <Button onClick={cargarCreditos} variant="outline" disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {/* Resumen */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Créditos</p>
                <p className="text-2xl font-bold">S/. {formatMonto(totalCreditos)}</p>
              </div>
              <CreditCard className="w-10 h-10 text-blue-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Cobrado</p>
                <p className="text-2xl font-bold text-green-600">S/. {formatMonto(totalPagado)}</p>
              </div>
              <CheckCircle2 className="w-10 h-10 text-green-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pendiente Cobrar</p>
                <p className="text-2xl font-bold text-amber-600">S/. {formatMonto(totalPendiente)}</p>
              </div>
              <Clock className="w-10 h-10 text-amber-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4 items-center">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar por orden, cliente, comprobante..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filtroEstado} onValueChange={setFiltroEstado}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="pendiente">Pendientes</SelectItem>
                <SelectItem value="pagado">Pagados</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tabla de Créditos */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Créditos ({creditosFiltrados.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Orden</TableHead>
                  <TableHead>Comprobante</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Pagado</TableHead>
                  <TableHead className="text-right">Pendiente</TableHead>
                  <TableHead>Progreso</TableHead>
                  <TableHead className="text-center">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-gray-400">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                      Cargando créditos...
                    </TableCell>
                  </TableRow>
                ) : creditosFiltrados.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-gray-400">
                      <CreditCard className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      No hay créditos para mostrar
                    </TableCell>
                  </TableRow>
                ) : creditosFiltrados.map((credito) => {
                  const porcentajePagado = credito.amount_total > 0 
                    ? Math.round((credito.total_pagado / credito.amount_total) * 100) 
                    : 0;
                  const estaCompleto = credito.saldo_pendiente <= 0.01;
                  
                  return (
                    <TableRow key={credito.id} className={estaCompleto ? 'bg-green-50' : ''}>
                      <TableCell className="text-sm">
                        {credito.date_order ? new Date(credito.date_order).toLocaleDateString('es-PE') : '-'}
                      </TableCell>
                      <TableCell className="font-mono text-sm font-medium text-indigo-600">
                        {credito.name}
                      </TableCell>
                      <TableCell className="text-sm">
                        {credito.tipo_comp && (
                          <Badge variant="outline" className="mr-1">{credito.tipo_comp}</Badge>
                        )}
                        {credito.num_comp || '-'}
                      </TableCell>
                      <TableCell className="text-sm">
                        <div className="flex items-center gap-1">
                          <User className="w-3 h-3 text-gray-400" />
                          <span className="truncate max-w-[150px]" title={credito.partner_name}>
                            {credito.partner_name || 'Cliente General'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        S/. {formatMonto(credito.amount_total)}
                      </TableCell>
                      <TableCell className="text-right font-mono text-green-600">
                        S/. {formatMonto(credito.total_pagado)}
                      </TableCell>
                      <TableCell className="text-right font-mono font-bold text-amber-600">
                        {estaCompleto ? (
                          <Badge className="bg-green-100 text-green-700">Pagado</Badge>
                        ) : (
                          `S/. ${formatMonto(credito.saldo_pendiente)}`
                        )}
                      </TableCell>
                      <TableCell className="w-32">
                        <div className="flex items-center gap-2">
                          <Progress value={porcentajePagado} className="h-2" />
                          <span className="text-xs text-gray-500 w-10">{porcentajePagado}%</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Button 
                          size="sm" 
                          variant={estaCompleto ? "ghost" : "outline"}
                          onClick={() => abrirModalPago(credito)}
                        >
                          <Wallet className="w-4 h-4 mr-1" />
                          {estaCompleto ? 'Ver' : 'Cobrar'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Modal Registrar Pago */}
      <Dialog open={pagoModalOpen} onOpenChange={setPagoModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-blue-600" />
              Gestionar Crédito
            </DialogTitle>
            <DialogDescription>
              Registra los pagos recibidos para este crédito
            </DialogDescription>
          </DialogHeader>
          
          {creditoSeleccionado && (
            <div className="space-y-4 mt-4">
              {/* Info del crédito */}
              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Orden:</span>
                  <span className="font-semibold">{creditoSeleccionado.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Cliente:</span>
                  <span>{creditoSeleccionado.partner_name || 'Cliente General'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total:</span>
                  <span className="font-bold">S/. {formatMonto(creditoSeleccionado.amount_total)}</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="text-gray-600">Pagado:</span>
                  <span className="text-green-600">S/. {formatMonto(creditoSeleccionado.total_pagado)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 font-medium">Pendiente:</span>
                  <span className="font-bold text-amber-600">S/. {formatMonto(creditoSeleccionado.saldo_pendiente)}</span>
                </div>
              </div>

              {/* Historial de pagos */}
              {pagosCredito.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    Pagos Registrados
                  </h4>
                  <div className="border rounded-lg overflow-hidden max-h-40 overflow-y-auto">
                    <Table>
                      <TableBody>
                        {pagosCredito.map((p) => (
                          <TableRow key={p.id}>
                            <TableCell className="text-sm">{p.fecha}</TableCell>
                            <TableCell className="text-sm">{p.cuenta_nombre}</TableCell>
                            <TableCell className="text-right font-mono text-green-600">
                              S/. {formatMonto(p.monto)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Formulario nuevo pago */}
              {creditoSeleccionado.saldo_pendiente > 0.01 && (
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    Registrar Pago
                  </h4>
                  <div className="space-y-3">
                    <Select value={nuevoPago.cuenta_id} onValueChange={(v) => setNuevoPago({...nuevoPago, cuenta_id: v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar cuenta" />
                      </SelectTrigger>
                      <SelectContent>
                        {cuentas.map(c => (
                          <SelectItem key={c.id} value={c.id.toString()}>
                            {c.nombre} ({c.moneda_codigo})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={nuevoPago.medio_pago} onValueChange={(v) => setNuevoPago({...nuevoPago, medio_pago: v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Medio de pago" />
                      </SelectTrigger>
                      <SelectContent>
                        {mediosPago.map(m => (
                          <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        placeholder="Monto"
                        value={nuevoPago.monto}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value) || 0;
                          // Limitar al saldo pendiente
                          if (val > creditoSeleccionado.saldo_pendiente) {
                            setNuevoPago({...nuevoPago, monto: creditoSeleccionado.saldo_pendiente.toFixed(2)});
                          } else {
                            setNuevoPago({...nuevoPago, monto: e.target.value});
                          }
                        }}
                        max={creditoSeleccionado.saldo_pendiente}
                        step="0.01"
                        className="flex-1"
                      />
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setNuevoPago({...nuevoPago, monto: creditoSeleccionado.saldo_pendiente.toFixed(2)})}
                        title="Pagar todo el saldo pendiente"
                      >
                        Total
                      </Button>
                    </div>
                    {parseFloat(nuevoPago.monto) > creditoSeleccionado.saldo_pendiente && (
                      <p className="text-xs text-red-500">El monto no puede exceder S/. {formatMonto(creditoSeleccionado.saldo_pendiente)}</p>
                    )}
                    <Input
                      placeholder="Nota (opcional)"
                      value={nuevoPago.nota}
                      onChange={(e) => setNuevoPago({...nuevoPago, nota: e.target.value})}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setPagoModalOpen(false)}>
              Cerrar
            </Button>
            {creditoSeleccionado?.saldo_pendiente > 0.01 && (
              <Button onClick={registrarPago} disabled={loadingPago}>
                {loadingPago ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Registrar Pago
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreditosPage;
